import type { MelodyGrid, MelodyMeter } from '@tunasong/schemas'
import { barBeats, beatMs, gridSlotBeats, gridSlotMs } from './grid.js'

/**
 * Find the beat the singer was actually on.
 *
 * Free-tempo capture (§11) has no click, so the grid is chosen AFTER the performance: the user taps
 * the pulse against playback of their own take, and this function then slides and stretches that
 * tapped grid until it best explains the ONSETS. That refinement is the whole reason the feature is
 * honest - a tap carries its own timing error, and a grid taken straight from the taps would make
 * every `offsetMs` in the melody a description of the tapping rather than of the singing.
 *
 * Pure: milliseconds in, a tempo and a phase out. No audio, no clock, no React.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §11.2, §11.4, §11.5
 */

export interface BeatGridFit {
  bpm: number
  /** Where beat 1 falls, in ms on the onsets' own clock. */
  phaseMs: number
  /** RMS distance from each onset to its nearest slot on the fitted grid. §11.4's fit quality. */
  rmsDeviationMs: number
}

export interface FitBeatGridOptions {
  /** Median inter-tap interval, as a tempo. The centre of the tempo search. */
  tappedBpm: number
  /** The first tap, which the user was asked to place on the downbeat. The phase search's centre,
   *  the tempo prior's origin, and the only thing that can choose between two grids one slot apart. */
  tappedPhaseMs: number
}

/** §11.2: the tempo search runs within ±15 % of the tapped value. */
export const FIT_TEMPO_RANGE = 0.15

/**
 * The resolutions the grid is fitted at, and the reason the cost below is scaled.
 *
 * §11.2 step 3 says to fit at the chosen grid resolution and step 4 caps that at a sixteenth, which
 * leaves the obvious reading: fit at a sixteenth. That does not work, and the failure is worth
 * naming because it is not obvious. **The finer the grid, the more freedom a WRONG tempo has.** A
 * sixteenth grid offers four slots a beat, so a tempo three per cent out can still put every onset
 * near some slot, and the residual it leaves can be smaller than the truth's. Measured on the §8
 * fixture: a sixteen-note take whose true tempo leaves 24.6 ms of lean was read at 116 bpm leaving
 * 25.7 ms, and the whole melody accelerated. An eight-note take at 120 was read as a limping
 * five-then-four-sixteenths pattern at 135 leaving 9.8 ms. Both readings are real; the onsets alone
 * cannot choose, and taking the smaller residual takes the wrong one.
 *
 * So a resolution is judged by how tightly the onsets sit on it RELATIVE TO ITS OWN SLOT, by
 * scaling every cost to the sixteenth slot at the same tempo. Onsets scattered at random inside a
 * slot score the same on any resolution afterwards, so a finer grid has to actually be tighter to
 * win rather than merely denser - and the straight/triplet choice, which is what §11.2 step 3's
 * "do not fit against beats alone" is protecting, still costs nothing.
 *
 * Musically this is the thing that makes a melody in eighths read as eighths, rather than as
 * sixteenths with every other slot left empty (Bowie, §11 review 2026-09-11).
 *
 * `'16t'` is absent because `chooseGrid` may never return it: fitting at a resolution the entity
 * cannot hold would buy the search freedom the model cannot spend.
 */
const FIT_RESOLUTIONS: MelodyGrid[] = ['8', '16', '8t']

/** What every resolution's cost is scaled to - the finest the grid is allowed to be (§11.2). */
const REFERENCE_GRID: MelodyGrid = '16'

/**
 * Coarse tempo step, as a fraction of the tapped tempo. A CONSTANT §11 DID NOT GIVE.
 *
 * A relative tempo error `d` displaces the onset at beat `b` by `d * b` beats, so the cost basin
 * around the true tempo is roughly `d = 0.125 / b` wide - half a sixteenth slot at the last onset.
 * The longest take this feature allows is about 64 beats, which puts the narrowest basin at
 * 0.00195. 0.0005 puts four coarse samples inside it; a step the width of the basin itself could
 * step straight over the answer.
 */
const COARSE_TEMPO_STEP = 0.0005

/**
 * Coarse phase step, as a fraction of a beat. A phase error displaces every onset equally, so a far
 * coarser step suffices here than in tempo - the basin is half a slot wide at any take length.
 */
const COARSE_PHASE_STEP_BEATS = 1 / 64

/**
 * Local refinement: this many steps either side of the incumbent, this many rounds, shrinking the
 * step by `REFINE_SHRINK` each round. 5^4 = 625, which takes the tempo step to about 0.0001 BPM and
 * the phase step to about 0.015 ms - both far inside §11.5's 0.5 BPM and 5 ms.
 *
 * A local grid rather than a ternary search: the cost surface is periodic in phase and riddled with
 * local minima in tempo, so nothing here is unimodal.
 */
const REFINE_SPAN = 5
const REFINE_ROUNDS = 4
const REFINE_SHRINK = 5

/**
 * How far from the beat a musician's note sits without anyone calling it wrong, in ms.
 *
 * §6.2's "together" band - this codebase's own definition of on-the-beat, set by Bowie for the
 * Trainer. Used here as the expected size of an onset residual: a take whose notes are this far off
 * their slots is a take with feel, not a take on the wrong grid.
 */
const FEEL_SIGMA_MS = 25

/**
 * How precisely a handful of taps pins a tempo, as a fraction. A CONSTANT §11 DID NOT GIVE.
 *
 * A tap lands within about ±25 ms of the beat it is following (`FEEL_SIGMA_MS`, the same band). A
 * median over the intervals of `m` taps at a 500 ms pulse therefore carries roughly
 * `25 * sqrt(2) / sqrt(m - 1) / 500` of relative error - 2.7 % for eight taps. 3 % is that, rounded.
 */
const TAP_TEMPO_SIGMA = 0.03

/**
 * The tap is a MEASUREMENT, not only a bound - and this is the line that makes it one.
 *
 * §11.2 bounds the tempo search to ±15 % of the tap, and a bound alone is not enough. Least squares
 * at a sixteenth resolution will happily re-read an eight-note phrase with 40 ms of lean at 120 bpm
 * as a limping five-then-four-sixteenths pattern at 135, because the limp leaves SMALLER residuals -
 * measured on the §8 fixture, 9.8 ms against the truth's 24.6. Both readings are real; the onsets
 * cannot choose between them, and a fit that takes the smaller residual takes the wrong one, which
 * is precisely the silent decision §11.1 rejects. A refinement that can move 13 % is not a
 * refinement.
 *
 * So a candidate tempo is charged for leaving the tap. The weight is the ratio of the two
 * uncertainties above, and it says one thing in one sentence: **moving 3 % off the tapped tempo
 * costs the same as putting every note 25 ms off its slot.** Nothing is fitted to a fixture - both
 * numbers come from elsewhere - and because the onset term grows with the number of onsets while
 * this one does not, a long take still overrules the tap, which is what the 8 %-off case needs.
 */
const TAP_TEMPO_PRIOR = (FEEL_SIGMA_MS / TAP_TEMPO_SIGMA) ** 2

/** Float slack when comparing two costs that the arithmetic says should be identical, in ms². */
const COST_EPSILON = 1e-6

/** Sum of squared distances from each onset to its nearest slot, at one resolution. */
const costAt = (onsetsMs: number[], slotMs: number, phaseMs: number): number => {
  let total = 0
  for (const onset of onsetsMs) {
    const slots = (onset - phaseMs) / slotMs
    const deviation = (slots - Math.round(slots)) * slotMs
    total += deviation * deviation
  }
  return total
}

interface Cost {
  /** What the comparison is made on: the onset residuals plus the tempo prior. */
  cost: number
  /** The onset residuals alone. `rmsDeviationMs` is reported from this - the prior is not feel. */
  onsetCost: number
  /** The slot length of the resolution that achieved it - the period of the cost in phase. */
  slotMs: number
}

/** The best of the candidate resolutions: a triplet take must not be charged for being a triplet. */
const costOf = (onsetsMs: number[], bpm: number, phaseMs: number, tappedBpm: number): Cost => {
  const ms = beatMs(bpm)
  const referenceSlotMs = gridSlotBeats(REFERENCE_GRID) * ms
  const drift = (bpm - tappedBpm) / tappedBpm
  const prior = TAP_TEMPO_PRIOR * drift * drift

  let best: Cost = { cost: Number.POSITIVE_INFINITY, onsetCost: Number.POSITIVE_INFINITY, slotMs: ms }
  for (const grid of FIT_RESOLUTIONS) {
    const slotMs = gridSlotBeats(grid) * ms
    const onsetCost = costAt(onsetsMs, slotMs, phaseMs)
    /** Scaled to the reference slot, so a denser grid is not rewarded for being dense. */
    const scale = (referenceSlotMs / slotMs) ** 2
    if (onsetCost * scale + prior < best.cost) {
      best = { cost: onsetCost * scale + prior, onsetCost, slotMs }
    }
  }
  return best
}

const rmsOf = (squaredTotal: number, count: number): number => (count === 0 ? 0 : Math.sqrt(squaredTotal / count))

interface Candidate extends Cost {
  bpm: number
  phaseMs: number
}

export const fitBeatGrid = (onsetsMs: number[], options: FitBeatGridOptions): BeatGridFit => {
  const { tappedBpm, tappedPhaseMs } = options

  /*
   * A single onset can be put on a slot exactly by sliding the phase, and no onset has anything to
   * say about a tempo at all. Either way the tapped grid is the only answer there is evidence for.
   */
  if (onsetsMs.length < 2) {
    return {
      bpm: tappedBpm,
      phaseMs: tappedPhaseMs,
      rmsDeviationMs: rmsOf(costOf(onsetsMs, tappedBpm, tappedPhaseMs, tappedBpm).onsetCost, onsetsMs.length),
    }
  }

  let best: Candidate = {
    bpm: tappedBpm,
    phaseMs: tappedPhaseMs,
    ...costOf(onsetsMs, tappedBpm, tappedPhaseMs, tappedBpm),
  }

  const consider = (bpm: number, phaseMs: number) => {
    const candidate = costOf(onsetsMs, bpm, phaseMs, tappedBpm)
    if (candidate.cost < best.cost) {
      best = { bpm, phaseMs, ...candidate }
    }
  }

  /** §11.2: ±15 % of the tapped tempo, and a phase window of half a beat AT the tapped tempo. */
  const tappedBeatMs = beatMs(tappedBpm)
  let tempoStep = tappedBpm * COARSE_TEMPO_STEP
  let phaseStep = tappedBeatMs * COARSE_PHASE_STEP_BEATS

  const tempoSteps = Math.ceil((tappedBpm * FIT_TEMPO_RANGE) / tempoStep)
  const phaseSteps = Math.ceil(tappedBeatMs / 2 / phaseStep)
  for (let t = -tempoSteps; t <= tempoSteps; t++) {
    for (let p = -phaseSteps; p <= phaseSteps; p++) {
      consider(tappedBpm + t * tempoStep, tappedPhaseMs + p * phaseStep)
    }
  }

  const refine = () => {
    for (let round = 0; round < REFINE_ROUNDS; round++) {
      tempoStep /= REFINE_SHRINK
      phaseStep /= REFINE_SHRINK
      const from = best
      for (let t = -REFINE_SPAN; t <= REFINE_SPAN; t++) {
        for (let p = -REFINE_SPAN; p <= REFINE_SPAN; p++) {
          consider(from.bpm + t * tempoStep, from.phaseMs + p * phaseStep)
        }
      }
    }
  }

  refine()

  /*
   * The phase is only identifiable modulo one slot, and this is where the tap gets to say which one.
   *
   * Sliding the grid by exactly one slot leaves every residual unchanged, so the cost surface is
   * PERIODIC in phase: the ±half-beat window holds several optima the onsets cannot tell apart, and
   * which of them the sweep lands on is decided by where its samples happened to fall. Left alone
   * that returns a melody displaced by a sixteenth, silently and for no musical reason. The user
   * tapped a downbeat; the slot nearest that tap is the one they meant.
   */
  const laps = Math.round((tappedPhaseMs - best.phaseMs) / best.slotMs)
  if (laps !== 0) {
    const wrapped = best.phaseMs + laps * best.slotMs
    const cost = costOf(onsetsMs, best.bpm, wrapped, tappedBpm)
    /** Equal by construction; `<=` with slack because the equality is arithmetic, not float. */
    if (cost.cost <= best.cost + COST_EPSILON) {
      best = { bpm: best.bpm, phaseMs: wrapped, ...cost }
      tempoStep = tappedBpm * COARSE_TEMPO_STEP
      phaseStep = tappedBeatMs * COARSE_PHASE_STEP_BEATS
      refine()
    }
  }

  return { bpm: best.bpm, phaseMs: best.phaseMs, rmsDeviationMs: rmsOf(best.onsetCost, onsetsMs.length) }
}

/**
 * How far an onset may sit from a slot and still be said to be on it. A CONSTANT §11 DID NOT GIVE.
 *
 * §11.2 step 4's rule as written - "the finest resolution that leaves every note's |offsetMs| within
 * half a slot" - is satisfied by every grid, because a distance to the NEAREST slot is at most half
 * a slot by construction. The rule that can actually be implemented, and the one a triplet take
 * needs, is a tolerance.
 *
 * The ceiling is 1/12 of a beat: that is what separates a sixteenth slot (1/4) from a triplet eighth
 * (1/3), and above it the two grids stop being distinguishable at all (Bowie, §11 review
 * 2026-09-11). 1/16 of a beat is comfortably under that and, at any tempo up to 156 BPM, comfortably
 * over the capture chain's own ±13.7 ms onset resolution (§8). A take whose feel is wider than the
 * tolerance falls back to the cap, which is the safe superset for straight music.
 */
export const GRID_FIT_TOLERANCE_BEATS = 1 / 16

/**
 * Straight before triplet at equal fit (§11.2 step 4), and never finer than a sixteenth.
 *
 * The same three grids the fit measures at, in the same order, and deliberately the SAME constant:
 * `FIT_RESOLUTIONS`' doc argues that `'16t'` is excluded here because `chooseGrid` can never return
 * it, and two literals holding that invariant apart would eventually drift.
 *
 * `'8'` first because a pulse read as eighths is the plain reading; `'16'` before `'8t'` because the
 * straight refinement is preferred where both would fit; `'8t'` only when neither straight grid can
 * explain the onsets. `'16'` is also the fallback when nothing explains the take, because a grid has
 * to be returned and the finest allowed one loses the least.
 */

export const chooseGrid = (onsetsMs: number[], bpm: number, phaseMs: number): MelodyGrid => {
  const ms = beatMs(bpm)
  const toleranceMs = GRID_FIT_TOLERANCE_BEATS * ms

  for (const grid of FIT_RESOLUTIONS) {
    const slotMs = gridSlotBeats(grid) * ms
    const fits = onsetsMs.every(onset => {
      const slots = (onset - phaseMs) / slotMs
      return Math.abs(slots - Math.round(slots)) * slotMs <= toleranceMs
    })
    if (fits) {
      return grid
    }
  }
  return '16'
}

export interface PickupOriginOptions {
  bpm: number
  meter: MelodyMeter
  grid: MelodyGrid
}

/**
 * Make room for an anacrusis: move the grid's origin back in whole BARS until nothing is early.
 *
 * A melody can begin before its first downbeat - "Happy Birthday" does - and in a free take the user
 * taps beat 1 of the first FULL bar, so the pickup necessarily precedes the tap. §5's quantizer
 * drops such a note as `before-take` and offers it back a slot later, which is right for a clicked
 * take (the count-in defines where the take starts) and wrong here: it throws away the beginning of
 * the tune. Bowie's §11 ruling, 2026-09-11 - **never drop a note for being early in a free take.**
 *
 * The shift is in bars and not beats so the tapped downbeat stays a downbeat: it becomes beat 1 of
 * bar 2 (or later) and the pickup fills the end of the bar before it, which is how a pickup is
 * written.
 *
 * The threshold is half a slot rather than the origin itself, because that is the quantizer's own
 * rule: a note three milliseconds before the downbeat is a note ON the downbeat with a lean, and
 * prepending an empty bar for it would invent a bar the singer never sang.
 */
export const originForPickup = (phaseMs: number, onsetsMs: number[], options: PickupOriginOptions): number => {
  const { bpm, meter, grid } = options
  if (onsetsMs.length === 0) {
    return phaseMs
  }

  const earliestKept = phaseMs - gridSlotMs(grid, bpm) / 2
  const barMs = barBeats(meter) * beatMs(bpm)
  const bars = Math.ceil((earliestKept - Math.min(...onsetsMs)) / barMs)

  return bars > 0 ? phaseMs - bars * barMs : phaseMs
}

/** §11.4's fit-quality bands. Under 25 ms the grid fits; over 60 ms it does not. */
export const FIT_GOOD_MAX_MS = 25
export const FIT_LOOSE_MAX_MS = 60

export type CaptureFitVerdict = 'capture-fit-good' | 'capture-fit-loose' | 'capture-fit-poor'

/**
 * A bad fit is NOT an error state (§11.4). The notes are kept exactly where they were sung and the
 * offsets carry the difference; this only decides which of three sentences the user reads.
 */
export const captureFitVerdict = (rmsDeviationMs: number): CaptureFitVerdict => {
  if (rmsDeviationMs < FIT_GOOD_MAX_MS) {
    return 'capture-fit-good'
  }
  return rmsDeviationMs <= FIT_LOOSE_MAX_MS ? 'capture-fit-loose' : 'capture-fit-poor'
}
